import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Alert, Box, Button, CircularProgress, Paper, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { toUserMessage } from '@/lib/errors';

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  compact?: boolean;
}

export function EmptyState({ title, description, icon, action, secondaryAction, compact }: EmptyStateProps) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: compact ? 3 : { xs: 4, md: 6 }, textAlign: 'center', borderStyle: 'dashed', bgcolor: 'transparent' }}
    >
      <Box sx={{ color: 'text.secondary', mb: 1.5, '& svg': { fontSize: compact ? 36 : 48 } }} aria-hidden>
        {icon ?? <InboxOutlinedIcon />}
      </Box>
      <Typography variant="h6" component="p">
        {title}
      </Typography>
      {description && (
        <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}>
          {description}
        </Typography>
      )}
      {(action || secondaryAction) && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3, justifyContent: 'center' }}>
          {action}
          {secondaryAction}
        </Stack>
      )}
    </Paper>
  );
}

export function LoadingState({ label = 'Loading…', variant = 'spinner', rows = 3 }: { label?: string; variant?: 'spinner' | 'skeleton'; rows?: number }) {
  if (variant === 'skeleton') {
    return (
      <Stack spacing={1.5} aria-busy="true" aria-label={label}>
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={72} />
        ))}
      </Stack>
    );
  }
  return (
    <Stack role="status" aria-live="polite" spacing={2} sx={{ py: 8, alignItems: 'center' }}>
      <CircularProgress />
      <Typography color="text.secondary">{label}</Typography>
    </Stack>
  );
}

export function ErrorState({ error, title = "We couldn't load this", onRetry }: { error: unknown; title?: string; onRetry?: () => void }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }} role="alert">
      <ErrorOutlineIcon color="error" sx={{ fontSize: 44, mb: 1 }} aria-hidden />
      <Typography variant="h6" component="p">
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>
        {toUserMessage(error)}
      </Typography>
      {onRetry && (
        <Button sx={{ mt: 3 }} variant="outlined" onClick={onRetry}>
          Try again
        </Button>
      )}
    </Paper>
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      {toUserMessage(error)}
    </Alert>
  );
}
